/**
 * Market hours utility for TradeLab.
 * Trading runs Sunday 4 PM UTC-6 (22:00 UTC) through Friday 3 PM UTC-6 (21:00 UTC).
 * Each trading day: 22:00 UTC → 21:00 UTC next day.
 * Daily maintenance: 21:00-22:00 UTC (3-4 PM UTC-6).
 */

export const TRADING_DAY_START_UTC = 22; // 4 PM UTC-6
export const TRADING_DAY_END_UTC = 21;   // 3 PM UTC-6

export type MarketStatus = 'open' | 'maintenance' | 'weekend' | 'pre_open';

/**
 * Get the current trading day boundaries in UTC.
 * A trading day runs from 22:00 UTC to 21:00 UTC next day.
 */
export function getCurrentTradingDay(now?: Date): { start: Date; end: Date; label: string } {
  const d = now ? new Date(now) : new Date();
  const utcHour = d.getUTCHours();

  let dayStart: Date;
  let dayEnd: Date;

  if (utcHour < TRADING_DAY_END_UTC) {
    // Before 21:00 UTC → trading day started yesterday at 22:00
    dayStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1, TRADING_DAY_START_UTC, 0, 0));
    dayEnd = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), TRADING_DAY_END_UTC, 0, 0));
  } else if (utcHour >= TRADING_DAY_START_UTC) {
    // At/after 22:00 UTC → new trading day just started
    dayStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), TRADING_DAY_START_UTC, 0, 0));
    dayEnd = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1, TRADING_DAY_END_UTC, 0, 0));
  } else {
    // 21:00-22:00 UTC maintenance window → show previous trading day
    dayStart = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - 1, TRADING_DAY_START_UTC, 0, 0));
    dayEnd = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), TRADING_DAY_END_UTC, 0, 0));
  }

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const label = days[dayEnd.getUTCDay()];

  return { start: dayStart, end: dayEnd, label };
}

/**
 * Get current trading week boundaries.
 * Sunday 22:00 UTC → Friday 21:00 UTC.
 */
export function getCurrentTradingWeek(now?: Date): { start: Date; end: Date } {
  const d = now ? new Date(now) : new Date();
  const dayOfWeek = d.getUTCDay(); // 0=Sun
  const utcHour = d.getUTCHours();

  // Walk back to most recent Sunday
  const sundayDate = new Date(d);
  sundayDate.setUTCDate(d.getUTCDate() - dayOfWeek);
  sundayDate.setUTCHours(0, 0, 0, 0);

  // If Sunday before 22:00 UTC, the trading week hasn't started yet → previous week
  if (dayOfWeek === 0 && utcHour < TRADING_DAY_START_UTC) {
    sundayDate.setUTCDate(sundayDate.getUTCDate() - 7);
  }

  const weekStart = new Date(Date.UTC(
    sundayDate.getUTCFullYear(), sundayDate.getUTCMonth(), sundayDate.getUTCDate(),
    TRADING_DAY_START_UTC, 0, 0
  ));

  // Friday = Sunday + 5
  const weekEnd = new Date(Date.UTC(
    sundayDate.getUTCFullYear(), sundayDate.getUTCMonth(), sundayDate.getUTCDate() + 5,
    TRADING_DAY_END_UTC, 0, 0
  ));

  return { start: weekStart, end: weekEnd };
}

/**
 * Check if market is currently open for trading.
 */
export function isMarketOpen(now?: Date): boolean {
  const d = now ? new Date(now) : new Date();
  const day = d.getUTCDay();
  const hour = d.getUTCHours();

  // Saturday: always closed
  if (day === 6) return false;

  // Sunday: only open after 22:00 UTC
  if (day === 0) return hour >= TRADING_DAY_START_UTC;

  // Friday: closes at 21:00 UTC
  if (day === 5) return hour < TRADING_DAY_END_UTC;

  // Mon-Thu: closed during maintenance 21:00-22:00 UTC
  return hour < TRADING_DAY_END_UTC || hour >= TRADING_DAY_START_UTC;
}

/**
 * Get detailed market status.
 */
export function getMarketStatus(now?: Date): MarketStatus {
  const d = now ? new Date(now) : new Date();
  const day = d.getUTCDay();
  const hour = d.getUTCHours();

  // Saturday
  if (day === 6) return 'weekend';

  // Sunday before market open
  if (day === 0 && hour < TRADING_DAY_START_UTC) return 'pre_open';

  // Friday after market close
  if (day === 5 && hour >= TRADING_DAY_END_UTC) return 'weekend';

  // Maintenance window (21:00-22:00 UTC) Mon-Thu
  if (hour >= TRADING_DAY_END_UTC && hour < TRADING_DAY_START_UTC) return 'maintenance';

  return 'open';
}
